<script lang="ts" setup>
import { ref } from 'vue';
import type { Escalation } from '@/gql/graphql';
import type { BottleneckItem } from './BottleneckTable.vue';
import { formatDate } from '@/utils/date';
import { VIcon, VMenu, VList, VListItem } from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import EscalationDeleteDialog from './EscalationDeleteDialog.vue';
import EscalationUpdateModal from './EscalationUpdateModal.vue';
import BottleneckSolveModal from './BottleneckSolveModal.vue';
import { BottleneckStatus } from '@/gql/graphql';
import BottleneckStatusComponent from './BottleneckStatus.vue';

const { t } = useI18n({
    messages: {
        nl: {
            pending: 'Te voltooien',
            finished: 'Afgerond',
            bottleneck: 'Bottleneck',
            escalation: 'Escalatie',
            solved: 'Opgelost',
            solve: 'Oplossen',
            edit: 'Bewerken',
            active: 'Actief',
            delete: 'Verwijderen',
        },
        en: {
            pending: 'Pending',
            finished: 'Finished',
            bottleneck: 'Bottleneck',
            escalation: 'Escalation',
            solved: 'Solved',
            solve: 'Solve',
            edit: 'Edit',
            active: 'Active',
            delete: 'Delete',
        },
    },
});

const props = defineProps<{
    item: Escalation;
    bottleneck: BottleneckItem;
    canUpdate: boolean;
    canEscalate: boolean;
    columns?: string[];
}>();

const isShowingEditModal = ref(false);
const isShowingSolveModal = ref(false);
</script>

<template>
    <EscalationUpdateModal
        v-model="isShowingEditModal"
        :bottleneck="props.bottleneck as Required<BottleneckItem>"
    />
    <BottleneckSolveModal
        v-model="isShowingSolveModal"
        :bottleneck="props.bottleneck"
        :solving-escalation="true"
    />
    <tr class="v-data-table__tr--expansion bottleneck-table__escalation-row">
        <td
            v-if="!columns || columns.includes('status')"
            class="v-data-table__td"
        >
            <BottleneckStatusComponent
                v-if="item.solvedAt"
                :status="BottleneckStatus.SOLVED"
            />
        </td>
        <td
            v-if="!columns || columns.includes('name')"
            class="v-data-table__td"
        >
            <span class="d-flex align-center text-error-20">
                <VIcon icon="$subdirectory" />
                <span>{{ item.name }}</span>
            </span>
        </td>
        <td
            v-if="!columns || columns.includes('description')"
            class="v-data-table__td"
        >
            {{ item.description }}
        </td>
        <td
            v-if="!columns || columns.includes('solution')"
            class="v-data-table__td"
        >
            {{ item.solution || item.expectedSolution }}
        </td>
        <td
            v-if="!columns || columns.includes('resolutionDate')"
            class="v-data-table__td"
        >
            <span v-if="!item.solvedAt">{{
                formatDate(item.expectedResolutionDate)
            }}</span>
            <span v-else>{{ formatDate(item.solvedAt) }}</span>
        </td>
        <td class="v-data-table__td"></td>
        <td class="v-data-table__td" colspan="2">
            <div v-if="canUpdate && canEscalate" class="d-flex justify-end">
                <v-menu v-if="!item.solvedAt" :close-on-content-click="true">
                    <template #activator="{ props: activatorProps }">
                        <v-btn-icon
                            size="x-small"
                            icon="$more"
                            v-bind="activatorProps"
                        />
                    </template>
                    <v-list>
                        <v-list-item @click="isShowingSolveModal = true">
                            {{ t('solve') }}
                        </v-list-item>
                        <v-list-item @click="isShowingEditModal = true">
                            {{ t('edit') }}
                        </v-list-item>
                        <EscalationDeleteDialog :escalation-id="item.id">
                            <template #activator="{ props: activatorProps }">
                                <v-list-item v-bind="activatorProps">
                                    {{ t('delete') }}
                                </v-list-item>
                            </template>
                        </EscalationDeleteDialog>
                    </v-list>
                </v-menu>
            </div>
        </td>
    </tr>
</template>

<style lang="scss" scoped>
.bottleneck-table__escalation-row {
    background-color: rgb(var(--v-theme-error-99));
}
</style>
